import { useEffect, useState, type ReactElement } from "react";
import {
  KAIZEN_DUE_FILTERS,
  KAIZEN_STAGE_FILTERS,
  type KaizenStageFilter,
  type PersonalKaizenListInput,
} from "@playbookai/widget-schemas";
import { VSM_PLAN_STATUSES, type VsmPlanStatus } from "@playbookai/types";
import { Checkbox } from "../../atoms/Checkbox/Checkbox.js";
import { RadioGroup } from "../../atoms/RadioGroup/RadioGroup.js";
import { Select } from "../../molecules/Select/Select.js";
import { FilterOptionList } from "../../molecules/FilterMenu/FilterOptionList.js";
import { useAccountOwnerOptions } from "../use-account-owner-options.js";
import type { DashboardWidgetEditorProps } from "../types.js";
import { useVsmCatalog } from "../vsm-catalog.js";
import { CatalogPickerControls } from "../CatalogPickerControls.js";

const DUE_LABELS = {
  any: "Any due date",
  overdue: "Overdue",
  next_7_days: "Next 7 days",
  next_30_days: "Next 30 days",
};
const STAGE_LABELS: Record<KaizenStageFilter, string> = {
  any: "Any stage",
  promoted: "Promoted",
  "not-promoted": "Not promoted",
};
const STATUS_LABELS: Record<VsmPlanStatus, string> = {
  "on-track": "On track",
  "at-risk": "At risk",
  blocked: "Blocked",
  done: "Done",
};
const EMPTY_IDS: readonly string[] = [];

export function KaizenTableEditor({
  draftParams,
  onChange,
  onValidityChange,
}: DashboardWidgetEditorProps): ReactElement {
  const catalog = useVsmCatalog();
  const filters = draftParams as Partial<PersonalKaizenListInput>;
  const owner = filters.owner ?? "any";
  const ownerIds = filters.ownerIds ?? EMPTY_IDS;
  const stage = filters.stage ?? "any";
  const planStatuses = [...new Set(filters.planStatuses ?? VSM_PLAN_STATUSES)];
  const dueState = filters.dueState ?? "any";
  const vsmIds = filters.vsmIds ?? EMPTY_IDS;
  const [specificScope, setSpecificScope] = useState(vsmIds.length > 0);
  const ownerOptions = useAccountOwnerOptions(ownerIds);
  const activate = catalog?.activate;
  const scopeLocked = catalog?.scopeLocked;
  useEffect(() => {
    if (!scopeLocked && specificScope) activate?.(vsmIds);
  }, [activate, scopeLocked, specificScope, vsmIds]);
  useEffect(() => {
    onValidityChange?.(
      specificScope && !scopeLocked && vsmIds.length === 0
        ? "Choose at least one value stream, or use All accessible."
        : owner === "selected" && ownerIds.length === 0
          ? "Choose at least one owner, or use Anyone."
          : null,
    );
  }, [
    onValidityChange,
    specificScope,
    scopeLocked,
    vsmIds.length,
    owner,
    ownerIds.length,
  ]);
  const commit = (patch: Record<string, unknown>): void =>
    onChange({
      ...draftParams,
      owner,
      stage,
      planStatuses,
      dueState,
      ...patch,
    });
  return (
    <div className="space-y-5">
      {catalog && !scopeLocked && (
        <fieldset className="space-y-3">
          <legend className="mb-2 text-sm font-semibold text-pb-text">
            Value streams
          </legend>
          <RadioGroup
            aria-label="Value stream scope"
            className="grid grid-cols-2"
            value={specificScope ? "specific" : "all"}
            options={[
              { value: "all", label: "All accessible" },
              { value: "specific", label: "Choose specific" },
            ]}
            onChange={(mode) => {
              setSpecificScope(mode === "specific");
              if (mode === "all") commit({ vsmIds: [] });
            }}
          />
          {specificScope ? (
            <CatalogPickerControls
              label="value streams"
              requireSearch
              query={catalog.searchQuery}
              isLoading={catalog.isLoading}
              isLoadingMore={catalog.isLoadingMore}
              error={catalog.error}
              hasMore={catalog.hasMore}
              isEmpty={catalog.vsms.length === 0}
              onSearchChange={catalog.onSearchChange}
              onLoadMore={catalog.onLoadMore}
              onRetry={catalog.onRetry}
              items={catalog.vsms.map((vsm) => ({
                id: vsm.id,
                label: vsm.name,
              }))}
              selectedIds={vsmIds}
              maxSelected={24}
              onSelectionChange={(ids) => commit({ vsmIds: ids })}
            />
          ) : (
            <p className="text-dense-1 text-pb-text-muted">
              Includes improvements from every value stream you can access.
            </p>
          )}
        </fieldset>
      )}
      <fieldset className="space-y-3">
        <legend className="mb-2 text-sm font-semibold text-pb-text">
          Improvement owners
        </legend>
        <RadioGroup
          aria-label="Improvement owners"
          className="grid grid-cols-2"
          value={owner}
          options={[
            { value: "any", label: "Anyone" },
            { value: "me", label: "Me" },
            {
              value: "selected",
              label: "Specific people",
              ...(!ownerOptions.available
                ? { disabledReason: "People search is unavailable here." }
                : {}),
            },
            { value: "unassigned", label: "Unassigned" },
          ]}
          onChange={(value) =>
            commit({
              owner: value,
              ownerIds: value === "selected" ? ownerIds : [],
            })
          }
        />
        {owner === "selected" && (
          <FilterOptionList
            source={ownerOptions.source}
            label="owners"
            multiple
            values={ownerIds}
            maxSelected={24}
            selectedLabel={ownerOptions.selectedLabel}
            onChange={(ids) => commit({ ownerIds: [...ids] })}
          />
        )}
      </fieldset>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Select
          label="Stage"
          value={stage}
          options={KAIZEN_STAGE_FILTERS.map((value) => ({
            value,
            label: STAGE_LABELS[value],
          }))}
          onChange={(value) => commit({ stage: value })}
        />
        <Select
          label="Due date"
          value={dueState}
          options={KAIZEN_DUE_FILTERS.map((value) => ({
            value,
            label: DUE_LABELS[value],
          }))}
          onChange={(value) => commit({ dueState: value })}
        />
      </div>
      <fieldset className="space-y-2">
        <legend className="mb-2 text-sm font-semibold text-pb-text">
          Plan status
        </legend>
        <div className="grid grid-cols-2 gap-3">
          {VSM_PLAN_STATUSES.map((status) => (
            <Checkbox
              key={status}
              label={STATUS_LABELS[status]}
              checked={planStatuses.includes(status)}
              disabled={
                planStatuses.includes(status) && planStatuses.length === 1
              }
              onChange={() =>
                commit({
                  planStatuses: planStatuses.includes(status)
                    ? planStatuses.filter((candidate) => candidate !== status)
                    : [...planStatuses, status],
                })
              }
            />
          ))}
        </div>
        <p className="text-dense-1 text-pb-text-muted">
          Selecting all statuses also includes improvements that have not been
          promoted.
        </p>
      </fieldset>
    </div>
  );
}
